/**
 * Shared helpers for adjustment builder forms to keep End Date >= Start Date
 * enforcement consistent across every form.
 */

import { format } from 'date-fns';

export const END_BEFORE_START_MESSAGE = 'End date must be on or after the start date';

/** Returns a valid Date for the given value, or null when missing/unparseable. */
export function parseDate(value: Date | string | number | null | undefined): Date | null {
  if (value === null || value === undefined || value === '') return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * Formats a date value, returning `fallback` when the value is missing or
 * invalid. Guards against `RangeError: Invalid time value` from date-fns
 * `format` — e.g. an adjustment whose original due date was never persisted.
 */
export function safeFormatDate(
  value: Date | string | number | null | undefined,
  dateFormat: string,
  fallback = '—',
): string {
  const d = parseDate(value);
  return d ? format(d, dateFormat) : fallback;
}

/** True when both dates are set and the end date falls before the start date. */
export function isEndBeforeStart(start: Date | undefined, end: Date | undefined): boolean {
  return !!(start && end && end < start);
}

/** Inline validation message rendered beneath an End Date field. */
export function EndDateError({ show }: { show: boolean }) {
  if (!show) return null;
  return <p className="text-xs text-destructive">{END_BEFORE_START_MESSAGE}</p>;
}
